import { createRouter, createWebHistory } from "vue-router";
import routes from "./paths";
import store from "@/store";
import {
  localStorageBoolean,
  localStorageKey,
  MODAL_NAME,
} from "@/utils/constants";
import eventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import { IModalMessage } from "@/interfaces/common/common";
import { getComponentUiText } from "@/utils/utilities";
import userService from "@/services/user/userService";
import { Invite, UserRoute } from "@/services/apiRoutes/routes";

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to) => {
  const isEditShareProfile = store.state.Sharing.isEdit;
  if (isEditShareProfile) {
    eventEmitter.emit(
      EmitterEvents.SHARING_CONFIRM_UPSERT_INCIDENT,
      to.fullPath,
    );
    return false;
  }
  // check for pre-prospect auth
  if (to.meta.requiresPreProspectAuth) {
    if (!store.state.Prospect.preProspectSession) {
      return { name: "ProspectHome" };
    }
  }

  // check for pre-user auth
  if (to.meta.requiresPreUserAuth) {
    if (!store.state.User.preSession) {
      return { name: "Login" };
    }
  }

  if (to.path.includes(Invite.inviteInternalCallback)) {
    const email = to.query.recipientEmail;
    if (store.state.User.isAuthenticated) {
      const currentUser = localStorage.getItem(localStorageKey.currentUserData);
      if (currentUser) {
        const user = JSON.parse(currentUser);
        if (user.email === email) {
          return { name: "ManageAccess" };
        } else {
          const emitData: IModalMessage = {
            component: MODAL_NAME.MODAL_RECOMMEN_ARE_YOU_SURE,
            properties: {
              value: {},
              title: getComponentUiText(
                "app-setting-org-manage-modal-incorrect-email-title-1",
              ),
              description: getComponentUiText(
                "app-setting-org-manage-modal-incorrect-email-description-1",
                false,
                { email: `${email}` || "" },
              ),
              buttonText: getComponentUiText("app-header-nav-log-out-title-1"),
              additionalNote: "",
              hiddenHelp: false,
              buttonRed: true,
              blueText: getComponentUiText(
                "app-settings-domain-delete-modal-action-cancel-1",
              ),
              containClass: "!tw-pt-[24px]",
            },
            events: {
              "update:data": async () => {
                await userService.logout();
                store.commit("User/SET_AVATAR_CURRENT", null);
                router.push({ name: "Login", query: { email } });
              },
            },
          };
          eventEmitter.emit(EmitterEvents.OPEN_MODAL, emitData);
        }
      }
      return { name: "Login" };
    }
    return { name: "Login" };
  }

  if (
    to.path.includes(UserRoute.passwrodResetCallback) &&
    store.state.User.isAuthenticated
  ) {
    const emitData: IModalMessage = {
      component: MODAL_NAME.MODAL_RECOMMEN_ARE_YOU_SURE,
      properties: {
        value: {},
        title: getComponentUiText(
          "app-settings-team-members-profile-modal-pwd-action-title-1",
        ),
        description: getComponentUiText(
          "app-settings-team-members-profile-modal-pwd-action-description-1",
        ),
        buttonText: getComponentUiText("app-header-nav-log-out-title-1"),
        additionalNote: "",
        hiddenHelp: false,
        buttonRed: true,
        blueText: getComponentUiText(
          "app-settings-domain-delete-modal-action-cancel-1",
        ),
        containClass: "!tw-pt-[24px]",
      },
      events: {
        "update:data": async () => {
          await userService.logout();
          router.push({
            name: "ResetPasswordCallback",
            query: { token: to.query.token },
          });
        },
      },
    };
    eventEmitter.emit(EmitterEvents.OPEN_MODAL, emitData);
  }

  // check for prospect auth
  if (to.meta.requiresProspectAuth) {
    store.dispatch("User/unauthenticateUser");
    store.dispatch("User/unsetPreSession");
    // redirect the prospect to the prospect home page, if not authenticated
    if (!store.state.Prospect.isProspectAuthenticated) {
      return { name: "ProspectHome" };
    }
  }
  if (
    to.path.includes("user/verify/email/callback") ||
    to.name === "VerifyEmail" ||
    to.name === "VerifyDomain"
  ) {
    return true;
  }
  // check for user auth
  if (to.meta.requiresAuth) {
    store.dispatch("User/unsetPreSession");
    store.dispatch("Prospect/unauthenticateProspect");
    // redirect the user to the login page, if not authenticated
    if (!store.state.User.isAuthenticated) {
      // redirect the user to the login page
      return { name: "Login" };
    }
    const orgPlanDetails = localStorage.getItem(
      localStorageKey.isOrgPlanDetails,
    );
    if (
      store.state.User.isAuthenticated &&
      orgPlanDetails === localStorageBoolean.false &&
      to.name != "Dashboard"
    ) {
      return { name: "Dashboard" };
    }
  }

  // Allow access to the verifyorganisation route if the user is authenticated
  if (to.name === "verifyorganisation" && store.state.User.isAuthenticated) {
    return true;
  }
  // if user accessing public page while authenticated, navigate to welcome page
  if (
    store.state.User.isAuthenticated &&
    !to.meta.requiresAuth &&
    !to.meta.requiresProspectAuth &&
    !to.meta.requiresPreProspectAuth &&
    !to.meta.requiresPreUserAuth
  ) {
    // if (to.name === "verifyorganisation") {
    //   return { name: "verifyorganisation" };
    // }
    return { name: "Dashboard" };
  }
  if (to.meta.requiresAuth && to.name === "Landing") {
    if (process.env.VUE_APP_DISPLAY_WELCOME !== "true") {
      return { name: "Dashboard" };
    }
  }
});
router.afterEach(() => {
  document.title = process.env.VUE_APP_BROWSER_TITLE || "CyberSystem";
});

export default router;
